import styled from 'styled-components';
import { spacing, body2 } from 'ui/theme';
import { GridRow } from 'ui/indexPages/tables/grid';
import { CHECKBOX_STATUS } from 'ui/checkbox/checkboxStatus';
import { CheckboxGridCell, LastModifiedCell, type UserInfo } from 'ui/indexPages/tables/cells';
import { useSelectionContext } from '../../../contexts/SelectionContext';
import { type Asset, type User } from '../../../types';
import { TABLE_GRID } from '../constants';
import { ImagePreview } from '../common/ImagePreview';
import { getImageType, getFileSize } from '../../../utils/strings';
import { AssetActionMenu } from '../common/AssetActionMenu';
import { Badge, BADGE_VARIANTS } from 'ui/badge';
import { Radio } from 'ui/radio';

export const Cell = styled.div`
  align-items: center;
  justify-content: start;
  display: flex;
`;

const Data = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${spacing.quarter};
  height: 100%;
  padding: ${spacing.eighth};
  overflow: hidden;
`;

const Name = styled.div`
  ${body2};
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

export const NameCell = styled.div`
  display: flex;
  column-gap: ${spacing.base};
  align-items: center;
`;

export const RadioContainer = styled.div`
  display: flex;
  align-items: center;
`;

const getLastModifiedParams = (user?: User): UserInfo => {
  if (user) {
    const { fullName, avatarUrl, id } = user as User;
    return { fullName, avatarUrl, emailAddress: id };
  }
  return { emailAddress: '' };
};

export const TableRow = ({
  asset,
  isPanel,
  hasManageBaseTemplatesPermission,
}: {
  asset: Asset;
  isPanel: boolean;
  hasManageBaseTemplatesPermission: boolean;
}) => {
  const { id, createdAt, creatorUser } = asset;
  const { bulk, toggleBulkSelection, clearAll } = useSelectionContext();
  const isSelected = bulk.findIndex((item) => item.id === id) > -1;

  return (
    <GridRow gridLayout={TABLE_GRID} key={id}>
      {isPanel ? (
        <RadioContainer>
          <Radio
            isChecked={isSelected}
            value={isSelected}
            onClick={() => {
              clearAll();
              toggleBulkSelection({ ...asset, name: asset.assetName });
            }}
          />
        </RadioContainer>
      ) : hasManageBaseTemplatesPermission ? (
        <CheckboxGridCell
          data-test={`checkbox-${id}`}
          id={id}
          handleClick={() => toggleBulkSelection({ ...asset, name: asset.assetName })}
          status={isSelected ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED}
        />
      ) : (
        <div />
      )}

      <NameCell>
        <ImagePreview image={asset} height={48} width={56} />
        <Data data-test={`image-details-${asset.id}`}>
          <Name>{asset.assetName}</Name>
        </Data>
      </NameCell>
      <LastModifiedCell
        createdAt={createdAt}
        createdBy={getLastModifiedParams(creatorUser)}
        modifiedAt={asset.updatedAt}
      />
      <Cell>{getFileSize(asset.size)}</Cell>
      <Cell>
        <Badge variant={BADGE_VARIANTS.DRAFT}>{getImageType(asset.mimeType)}</Badge>
      </Cell>
      <AssetActionMenu
        asset={asset}
        isPanel={isPanel}
        hasManageBaseTemplatesPermission={hasManageBaseTemplatesPermission}
      />
    </GridRow>
  );
};
